import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../../services/contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import useSWR, { mutate } from 'swr'
import UserContractsTable from './UserContractsTable'
import { HeaderTypography, TableContainer } from './components'
import { getOperatorId } from '../../services/utils/helpers'
import { bffService } from '../../services/utils/axios'

function Contracts() {
  const { t } = useTranslation()
  const { token } = useAuth()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState('')
  const [contractTypeFilter, setContractTypeFilter] = useState('')
  const [contractStatusFilter, setContractStatusFilter] = useState('')
  const [shouldScrollAfterFetch, setShouldScrollAfterFetch] = useState(false)

  // Build the request query string. Pagination is sent to the server via
  // skip/take; the server response may or may not include a pagination
  // envelope (UserContractsTable handles both cases for the total count).
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    const operatorId = getOperatorId()
    params.append('order', '-createdAt')
    params.append('skip', page * rowsPerPage)
    params.append('take', rowsPerPage)
    if (operatorId) {
      params.append('company_id', operatorId)
    }

    // Use the search parameter for text search (searches in firstName, lastName, email, company name, and address fields)
    if (searchText && searchText.trim()) {
      params.append('search', searchText.trim())
    }

    if (contractTypeFilter && contractTypeFilter.trim()) {
      // Map filter values to API values
      if (contractTypeFilter === 'PRODUCER') {
        params.append('energy_role', 'PRODUCER')
      } else if (contractTypeFilter === 'CONSUMER') {
        params.append('energy_role', 'CONSUMER')
      }
    }

    if (contractStatusFilter && contractStatusFilter.trim()) {
      params.append('status', contractStatusFilter)
    }

    const queryString = params.toString()

    return queryString
  }, [
    page,
    rowsPerPage,
    searchText,
    contractTypeFilter,
    contractStatusFilter,
  ])

  // SWR cache key — changes to any of these trigger a refetch, including
  // page and rowsPerPage so pagination is fully server-driven.
  const swrKey = useMemo(() => {
    if (!token) return null
    return {
      path: `/contracts/grouped-by-users?${queryParams}`,
      filters: {
        searchText,
        contractTypeFilter,
        contractStatusFilter,
      },
      page,
      rowsPerPage,
    }
  }, [
    token,
    queryParams,
    searchText,
    contractTypeFilter,
    contractStatusFilter,
    page,
    rowsPerPage,
  ])

  const { data, isLoading, isValidating } = useSWR(swrKey, {
    revalidateOnMount: true,
    revalidateOnFocus: false, // Prevent refetch on window focus
    revalidateOnReconnect: false, // Prevent refetch on reconnect
    dedupingInterval: 1000, // Dedupe requests within 1 second
    keepPreviousData: true, // Keep previous data while fetching new data
    fetcher: async (key) => {
      const { data } = await bffService.get(key.path)
      return data
    },
  })

  // Monitor filter changes and ensure proper cache invalidation
  useEffect(() => {
    console.log('Filters changed, invalidating cache for:', swrKey?.path)
    if (swrKey) {
      // Invalidate the cache when filters change
      mutate(swrKey, undefined, { revalidate: true })
    }
  }, [swrKey])

  const scrollToAppContentTop = useCallback(() => {
    if (typeof document !== 'undefined') {
      const appContent = document.getElementById('app-content-scroll-container')
      if (appContent) {
        appContent.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    if (!shouldScrollAfterFetch) return
    if (isValidating) return
    scrollToAppContentTop()
    setShouldScrollAfterFetch(false)
  }, [shouldScrollAfterFetch, isValidating, scrollToAppContentTop])

  const handlePageChange = useCallback(
    (newPage) => {
      setPage(newPage)
      if (newPage > page) {
        setShouldScrollAfterFetch(true)
      }
    },
    [page]
  )

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage)
    setPage(0) // Reset to first page when changing rows per page
  }, [])

  const handleSearchText = useCallback((value) => {
    setSearchText(value)
    setPage(0) // Reset to first page when searching
  }, [])

  const handleContractTypeFilterChange = useCallback((value) => {
    setContractTypeFilter(value)
    setPage(0) // Reset to first page when filtering
  }, [])

  const handleContractStatusFilterChange = useCallback((value) => {
    setContractStatusFilter(value)
    setPage(0) // Reset to first page when filtering
  }, [])

  const handleResetFilters = useCallback(() => {
    setSearchText('')
    setContractTypeFilter('')
    setContractStatusFilter('')
    setPage(0) // Reset to first page when resetting filters
    // Force refetch by invalidating the cache
    if (swrKey) {
      mutate(swrKey, undefined, { revalidate: true })
    }
  }, [swrKey])

  // Memoize the table props to prevent unnecessary rerenders
  const tableProps = useMemo(
    () => ({
      contracts: data,
      page,
      rowsPerPage,
      searchText,
      contractTypeFilter,
      contractStatusFilter,
      isLoading,
      onPageChange: handlePageChange,
      onRowsPerPageChange: handleRowsPerPageChange,
      onSearchTextChanged: handleSearchText,
      onContractTypeFilterChanged: handleContractTypeFilterChange,
      onContractStatusFilterChanged: handleContractStatusFilterChange,
      onResetFiltersPressed: handleResetFilters,
    }),
    [
      data,
      page,
      rowsPerPage,
      searchText,
      contractTypeFilter,
      contractStatusFilter,
      isLoading,
      handlePageChange,
      handleRowsPerPageChange,
      handleSearchText,
      handleContractTypeFilterChange,
      handleContractStatusFilterChange,
      handleResetFilters,
    ]
  )

  return (
    <>
      <Helmet title="Nutzers" />

      <Grid container spacing={10} sx={{ visibility: 'transparent' }}>
        <Grid item mb={14} ml={4}>
          <HeaderTypography variant="h2" gutterBottom display="inline">
            {t('LBLUserMgmt')}
          </HeaderTypography>
        </Grid>
      </Grid>
      <TableContainer variant="outlined">
        <UserContractsTable {...tableProps} />
      </TableContainer>
    </>
  )
}

export default React.memo(Contracts)
